import Copy from '@nib-components/copy';
import Heading from '@nib-components/heading';
import { Stack } from '@nib/layout';
import { Field } from 'formik';
import React from 'react';
import styled from 'styled-components';
import { Margin } from '@nib/layout';
import { AddPartnerRadio, DateOfBirth, Email, FirstName, LastName, NestedFormFields, Phone, Sex, Title, TransferringFundsGroup } from '../..';
import { FUNNELS, INTERNATIONAL_FUNNELS, LABELS } from '../../util/constants';
import { formatDecimal } from '../../util/format-decimal';
import { getGroupEventHandlerProps } from '../../util/propTypes';
import { generateNameMask } from '../../util/validationHelpers';
import { AddFunnelPropFromContext } from '../funnel-context';
import ResidencyStatusGroup from '../partner-residency-status';
import { StandardInfoBox } from '../info-box';
const CURRENT_PROVIDER_LABEL = {
  ARHI: LABELS.PARTNER_TRANSFERRING_FUNDS_PROVIDER_LABEL_ARHI,
  GU: LABELS.PARTNER_TRANSFERRING_FUNDS_PROVIDER_LABEL,
  IMAN: LABELS.PARTNER_TRANSFERRING_FUNDS_PROVIDER_LABEL,
  IWHI: LABELS.PARTNER_TRANSFERRING_FUNDS_PROVIDER_LABEL,
  ISHI: LABELS.PARTNER_TRANSFERRING_FUNDS_PROVIDER_LABEL,
  IAP: LABELS.PARTNER_TRANSFERRING_FUNDS_PROVIDER_LABEL
};
const Hidden = styled.div`
  display: none;
`;
const PartnerGroup = props => {
  const {
    id = 'partnerAdd',
    name = 'partnerAdd',
    funnel = FUNNELS.IWHI,
    help = '',
    width = 20,
    optional = false,
    showPartnerTransferringFundsGroup = false,
    subHeading = 'Partner details',
    excludeAddPartnerRadio = false,
    showNestedLeftBorder = true,
    partnerLastNameMaxLength = 24,
    groupOnBlurHandlers = {},
    groupOnChangeHandlers = {},
    includeHiddenContactDetails = false,
    partnerPhoneOptional = true,
    partnerEmailOptional = true,
    partnerTransferFundsLabel,
    partnerFirstNameLabel,
    partnerFirstNameHelp,
    partnerLastNameLabel,
    partnerLastNameHelp,
    addPartnerRadioLabel,
    addPartnerRadioHelp,
    partnerNameMaxLength,
    values,
    partnerSexHelpText,
    currentProviderOptions,
    currentProviderSuggestions,
    partnerPhoneLabel,
    partnerPhoneHelp,
    partnerEmailLabel,
    partnerEmailHelp,
    includeResidencyStatus,
    hasPartnerResidencyMismatch,
    newPriceDateOfBirth,
    newPriceTransferringFunds,
    residencyStatusHelpText,
    formMode,
    enableContinuousCover,
    required
  } = props;
  const showSubfields = values[id] === 'yes' || excludeAddPartnerRadio;
  const eventHandlers = {
    groupOnBlurHandlers,
    groupOnChangeHandlers
  };
  const showPriceChangeNotification = !INTERNATIONAL_FUNNELS.includes(funnel);
  return React.createElement("div", null, !excludeAddPartnerRadio && React.createElement(Field, {
    name: name,
    id: id,
    component: AddPartnerRadio,
    label: addPartnerRadioLabel,
    help: addPartnerRadioHelp,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id
    })
  }), React.createElement(NestedFormFields, {
    expanded: showSubfields,
    id: `${name}--nestedFormFields`,
    subHeading: subHeading,
    help: help,
    label: props.label || LABELS[funnel] || LABELS.ARHI,
    showLeftBorder: showNestedLeftBorder,
    width: width,
    optional: optional
  }, React.createElement(Field, {
    required: required,
    name: "partnerTitle",
    id: "partnerTitle",
    component: Title,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'partnerTitle'
    })
  }), React.createElement(Field, {
    required: required,
    name: "partnerFirstName",
    id: "partnerFirstName",
    component: FirstName,
    label: partnerFirstNameLabel,
    help: partnerFirstNameHelp,
    maxLength: partnerNameMaxLength,
    mask: generateNameMask(partnerNameMaxLength),
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'partnerFirstName'
    })
  }), React.createElement(Field, {
    required: required,
    name: "partnerLastName",
    id: "partnerLastName",
    component: LastName,
    label: partnerLastNameLabel,
    help: partnerLastNameHelp,
    maxLength: partnerLastNameMaxLength,
    mask: generateNameMask(partnerLastNameMaxLength),
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'partnerLastName'
    })
  }), React.createElement(Field, {
    required: required,
    name: "partnerSex",
    id: "partnerSex",
    component: Sex,
    help: partnerSexHelpText,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'partnerSex'
    })
  }), React.createElement(Stack, {
    space: 2
  }, React.createElement(Field, {
    required: required,
    name: "partnerDateOfBirth",
    id: "partnerDateOfBirth",
    component: DateOfBirth,
    onDateBlur: groupOnBlurHandlers.partnerDateOfBirth,
    formMode: formMode
  }), showPriceChangeNotification && newPriceDateOfBirth && React.createElement(StandardInfoBox, {
    role: "status",
    withTip: true
  }, React.createElement(Copy, null, "Your price has been updated to reflect your partner's changed date of birth details.", ' ', React.createElement("strong", null, "New price $", formatDecimal(newPriceDateOfBirth, {
    places: 2,
    separator: ','
  }), "*.")))), includeResidencyStatus && React.createElement(ResidencyStatusGroup, {
    funnel: funnel,
    values: values,
    groupOnBlurHandlers: groupOnBlurHandlers,
    groupOnChangeHandlers: groupOnChangeHandlers,
    hasPartnerResidencyMismatch: hasPartnerResidencyMismatch,
    residencyStatusHelpText: residencyStatusHelpText
  }), !hasPartnerResidencyMismatch && includeHiddenContactDetails && React.createElement(Hidden, {
    id: "partnerContactDetails"
  }, React.createElement(Margin, {
    bottom: 6
  }, React.createElement(Heading, {
    size: 3
  }, "Contact details")), React.createElement(Margin, {
    bottom: 6
  }, React.createElement(Field, {
    required: required,
    name: "partnerPhone",
    id: "partnerPhone",
    component: Phone,
    label: partnerPhoneLabel,
    help: partnerPhoneHelp,
    optional: partnerPhoneOptional,
    maxLength: 10,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'partnerPhone'
    })
  })), React.createElement(Field, {
    required: required,
    name: "partnerEmail",
    id: "partnerEmail",
    component: Email,
    label: partnerEmailLabel,
    help: partnerEmailHelp,
    optional: partnerEmailOptional,
    maxLength: 60,
    ...getGroupEventHandlerProps({
      ...eventHandlers,
      id: 'partnerEmail'
    })
  }))), showSubfields && !hasPartnerResidencyMismatch && showPartnerTransferringFundsGroup && React.createElement(Margin, {
    top: 6
  }, React.createElement(TransferringFundsGroup, {
    required: required,
    values: values,
    name: "partnerTransferringFunds",
    id: "partnerTransferringFunds",
    label: partnerTransferFundsLabel || LABELS.PARTNER_TRANSFERRING_FUNDS_RADIO,
    infoBoxMessage: LABELS.PARTNER_TRANSFERRING_FUNDS_INFO_BOX,
    subHeading: LABELS.PARTNER_TRANSFERRING_FUNDS_SUB_HEADING,
    currentProviderHelp: "",
    currentProviderLabel: CURRENT_PROVIDER_LABEL[funnel] || CURRENT_PROVIDER_LABEL.ARHI,
    memberNumberLabel: LABELS.PARTNER_TRANSFERRING_FUNDS_MEMBER_NUMBER_LABEL,
    currentProviderOptions: currentProviderOptions,
    currentProviderSuggestions: currentProviderSuggestions,
    groupOnBlurHandlers: groupOnBlurHandlers.partnerTransferringFunds,
    groupOnChangeHandlers: groupOnChangeHandlers.partnerTransferringFunds,
    newPrice: newPriceTransferringFunds,
    enableContinuousCover: enableContinuousCover,
    isPartner: true
  })));
};
const PartnerGroupWithFunnel = props => React.createElement(AddFunnelPropFromContext, null, React.createElement(PartnerGroup, {
  ...props
}));
export default PartnerGroupWithFunnel;